import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { TextField, Button, Typography, Grid, Paper, Stack, Box, Snackbar, Alert, IconButton, CircularProgress, Slider, Accordion, AccordionSummary, AccordionDetails, AccordionActions, useTheme, LinearProgress } from "@mui/material";
import * as THREE from "three";
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { Close as CloseIcon, Expand, ExpandMore } from "@mui/icons-material";
import { useGetPart3DFilesQuery, useGetPartQuery } from "../rtk/image";
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader';
import { PMREMGenerator } from 'three';
import { EXRLoader } from 'three/examples/jsm/loaders/EXRLoader.js';
import { HexColorPicker } from "react-colorful";
import { hasPermission } from "../rtk/selectors";
import { useUpdatePartMutation } from "../rtk/system";
import { TextSprite } from "./TextSprite";
import { OBJLoader, STLLoader } from "three/examples/jsm/Addons.js";
// import { sRGBEncoding } from 'three'; 
// import { FontLoader, TextGeometry } from "three/examples/jsm/Addons.js";
import { VertexNormalsHelper } from 'three/addons/helpers/VertexNormalsHelper.js';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { SSAOPass } from 'three/examples/jsm/postprocessing/SSAOPass.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';
// import { LoopSubdivision } from 'three/examples/jsm/modifiers/LoopSubdivision.js';
import { LoopSubdivision } from 'three-subdivide';
import { mergeVertices } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { DRACOLoader } from "three/examples/jsm/Addons.js";
import api from "../api";

console.log(THREE.REVISION);


const intToHex = (intColor) => {
    const hex = intColor.toString(16).padStart(6, '0');
    return `#${hex}`;
};

const setupControlsForObject = (object, controls) => {
    const box = new THREE.Box3().setFromObject(object);
    const size = new THREE.Vector3();
    box.getSize(size);
    const maxDim = Math.max(size.x, size.y, size.z);

    // Adjust controls based on object size
    controls.enableDamping = true;
    controls.dampingFactor = 0.50;
    controls.enableZoom = true;
    controls.enablePan = true;

    // Scale movement speeds based on object size
    // const scale = 1.0 // Adjust scale factor as needed
    const scale = 1.0;//Math.max(0.2, Math.min(1.0, maxDim / 100)); // Adjust scale factor as needed

    // Adjust rotation speed
    controls.rotateSpeed = 0.5 * scale;

    // Adjust pan speed
    controls.panSpeed = 0.3 * scale;

    // Adjust zoom speed
    controls.zoomSpeed = 0.5 * scale;

    // Set zoom limits relative to object size
    // controls.minDistance = maxDim * 0.1;
    // controls.maxDistance = maxDim * 10;

    // Set pan limits (optional - keeps the object in view)
    // controls.minPolarAngle = 0;
    // controls.maxPolarAngle = Math.PI;

    const center = new THREE.Vector3();
    box.getCenter(center);
    controls.target.copy(center);
    controls.update();
};


const Part3DViewer = ({ partNumber, pageWidth, pageHeight, onClose }) => {
    const canvasRef = useRef(null);
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    const { data: part, isLoading: isPartLoading, isError, error: fetchError } = useGetPartQuery(partNumber, {
        skip: !partNumber,
        refetchOnMountOrArgChange: true,
    });
    const [updatePart, updateResponse] = useUpdatePartMutation();

    const { t } = useTranslation();

    const [color, setColor] = useState("#ffffff");
    const [metalness, setMetalness] = useState(0.5);
    const [roughness, setRoughness] = useState(0.5);
    const [emissiveColor, setEmissiveColor] = useState("#000000");
    const [emissiveIntensity, setEmissiveIntensity] = useState(0.0);

    const animationIdRef = useRef(null);
    const objectRef = useRef(null);
    const sceneRef = useRef(null);
    const rendererRef = useRef(null);
    const cameraRef = useRef(null);
    const [lastLoadedPartNumber, setLastLoadedPartNumber] = useState(null);


    const width = pageWidth || window.innerWidth;
    const height = pageHeight || window.innerHeight;

    const user = useSelector(state => state.user.user);
    const userHasPermission = useSelector(state => hasPermission(state, "can_manage_part"), [user]);

    const theme = useTheme();


    useEffect(() => {
        if (part) {
            setColor(part.color ? intToHex(part.color) : "#ffffff");
            setMetalness(part.metalness || 0.5);
            setRoughness(part.roughness || 0.5);
            setEmissiveColor(part.emissive_color || "#000000");
            setEmissiveIntensity(part.emissive_intensity || 0.0);
        }
    }, [part]);

    const autoZoom = (obj, controls, camera) => {
        if (!obj || !camera) return;

        // Compute bounding box of the object
        const box = new THREE.Box3().setFromObject(obj);
        const center = box.getCenter(new THREE.Vector3());
        const size = box.getSize(new THREE.Vector3());
        const maxDim = Math.max(size.x, size.y, size.z);

        // Set camera position and target
        const fov = camera.fov * (Math.PI / 180);
        const distance = maxDim / (2 * Math.tan(fov / 2));
        camera.position.copy(center.clone().add(new THREE.Vector3(0, 0, distance * 1.5)));
        camera.near = Math.max(0.01, distance / 100);
        camera.far = distance * 10 + maxDim;
        camera.updateProjectionMatrix();
        camera.lookAt(center);

        // Set controls target
        controls.target.copy(center);
        controls.update();
    };


    useEffect(() => {
        if (!partNumber || !part) {
            return;
        }
        if (objectRef.current && lastLoadedPartNumber === partNumber) {
            objectRef.current.traverse((child) => {
                if (child.isMesh && child.material) {
                    child.material.color.set(color);
                    child.material.metalness = metalness;
                    child.material.roughness = roughness;
                    child.material.emissive.set(emissiveColor);
                    child.material.emissiveIntensity = emissiveIntensity;
                    child.material.needsUpdate = true; // Ensure material updates
                }
            });
            return; // Exit early if the object is already loaded
        }
        const fetchAndLoad = async () => {
            try {
                setIsLoading(true);
                // Remove previous object from scene
                if (objectRef.current && sceneRef.current) {
                    sceneRef.current.remove(objectRef.current);
                    objectRef.current = null;
                }
                let response;
                try {
                    response = await api.getBlob(
                        `/part3dfiles/?part_number=${encodeURIComponent(partNumber)}`
                    );
                }
                catch (error) {
                    throw new Error("Failed to fetch 3D file");
                }
                const blob = response.data
                const url = URL.createObjectURL(blob);

                // --- Three.js setup ---
                const canvas = canvasRef.current;

                let scene = sceneRef.current;
                if (!scene) {
                    scene = new THREE.Scene();
                } else {
                    scene.clear(); // Clear the previous scene
                }
                sceneRef.current = scene; // Store the scene reference for later use

                let camera = cameraRef.current;
                if (!camera) {
                    camera = new THREE.PerspectiveCamera(75, width / height, 0.1, 2000);
                } else {
                    camera.aspect = width / height; // Update aspect ratio
                    camera.updateProjectionMatrix(); // Update projection matrix
                }
                cameraRef.current = camera; // Store the camera reference for later use

                let renderer = rendererRef.current;
                if (!renderer) {
                    renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: false });
                } else {
                    renderer.setSize(width, height); // Update size
                    renderer.setPixelRatio(window.devicePixelRatio); // Update pixel ratio
                }
                rendererRef.current = renderer; // Store the renderer reference for later use
                renderer.outputColorSpace = THREE.SRGBColorSpace;
                renderer.toneMapping = THREE.ACESFilmicToneMapping;
                renderer.toneMappingExposure = 1.0;
                renderer.shadowMap.enabled = true;
                renderer.shadowMap.type = THREE.PCFSoftShadowMap;
                theme.palette.mode === "dark" ? renderer.setClearColor(0x303030) : renderer.setClearColor(0xf0f0f0);
                // renderer.outputEncoding = THREE.; // Ensure colors are displayed correctly

                renderer.setSize(width, height);
                renderer.setPixelRatio(window.devicePixelRatio);
                canvas.appendChild(renderer.domElement);


                const loader = new GLTFLoader();
                loader.setDRACOLoader(new DRACOLoader().setDecoderPath('/draco/')); // Set the path to Draco decoder files
                const controls = new OrbitControls(camera, renderer.domElement);


                // const rgbeLoader = new RGBELoader();
                const pmremGenerator = new PMREMGenerator(renderer);
                pmremGenerator.compileEquirectangularShader();

                new EXRLoader().setDataType(THREE.HalfFloatType).load('/hdri/studio.exr', (hdrTexture) => {
                    // rgbeLoader.load('/hdri/studio.exr', (hdrTexture) => {
                    const envMap = pmremGenerator.fromEquirectangular(hdrTexture).texture;

                    scene.environment = envMap;
                    // scene.background = envMap; // optional, for visible background
                    hdrTexture.dispose();
                    pmremGenerator.dispose();

                    const partColor = part?.color || "#ffffff"; // Default to white if no color is provided
                    const partMetalness = part?.metalness || 0.5; // Default to 0.5 if no metalness is provided
                    const partRoughness = part?.roughness || 0.5; // Default to 0.5 if no roughness is provided
                    const partEmissive = part?.emissive_color || 0x000000; // Default to black if no emissive color is provided
                    const partEmissiveIntensity = part?.emissive_intensity || 0.0; // Default to 0 if no emissive intensity is provided


                    loader.load(
                        url,
                        (gltf) => {
                            gltf.scene.traverse((child) => {

                                if (child.isMesh) {

                                    // check if the geometry has normals, if not, compute them
                                    if (!child.geometry.attributes.normal) {
                                        // alert("Geometry has no normals, computing them.");
                                        child.geometry.computeVertexNormals();
                                    }

                                    // child.material = new THREE.MeshNormalMaterial();
                                    child.material.flatShading = false;
                                    child.castShadow = true;
                                    child.receiveShadow = true;

                                    // Create realistic metallic material with environment reflections
                                    child.material.metalness = partMetalness; // High metallic for realistic reflections
                                    child.material.roughness = partRoughness; // Low roughness for sharp reflections
                                    child.material.emissive = new THREE.Color(partEmissive); // Optional: add some emissive color for highlights
                                    child.material.emissiveIntensity = partEmissiveIntensity; // Adjust emissive intensity
                                    child.material.envMap = envMap; // Use the environment map for reflections
                                    child.material.envMapIntensity = 1.2; // Adjust intensity for better reflections
                                    child.material.needsUpdate = true; // Ensure material updatesè
                                    child.material.color.set(partColor); // Set a neutral color for better reflections
                                    child.material.side = THREE.DoubleSide; // Render both sides of the mesh

                                    // rotate that bad boy 
                                    child.rotation.x = -Math.PI / 2; // Rotate the mesh to face upwards
                                    // child.rotation.z = Math.PI; // Rotate the mesh to face the camera correctly

                                    // center the model
                                    const box = new THREE.Box3().setFromObject(child);
                                    const size = new THREE.Vector3();
                                    box.getSize(size);
                                    const center = new THREE.Vector3();
                                    box.getCenter(center);
                                    child.position.sub(center); // Center the mesh at the origin

                                    const thresholdAngle = 80; // degrees
                                    const edges = new THREE.EdgesGeometry(child.geometry, thresholdAngle);
                                    const outline = new THREE.LineSegments(edges, new THREE.LineBasicMaterial({ color: 0x000000, linewidth: 1, opacity: 0.4, transparent: true }));
                                    outline.renderOrder = 1; // Render on top
                                    child.add(outline); // Add outline as a child of the mesh

                                    // const helper = new VertexNormalsHelper(child, 1, 0xff0000);
                                    // scene.add(helper);


                                    setupControlsForObject(child, controls); // Setup controls based on object size
                                    autoZoom(child, controls, camera); // Center and zoom the camera to fit the model
                                }
                            });

                            // ...rest of centering code...
                            scene.add(gltf.scene);
                            objectRef.current = gltf.scene;
                            setLastLoadedPartNumber(partNumber); // Store the last loaded part number
                            renderer.render(scene, camera);
                            URL.revokeObjectURL(url);
                            setIsLoading(false);
                        },
                        undefined,
                        (err) => {
                            console.error("Error loading 3D model:", err);
                            setError("Error loading 3D model");
                        }
                    );
                });

                // Add orbit controls
                controls.enableDamping = true; // an animation loop is required when either damping or auto-rotation are enabled
                controls.dampingFactor = 0.50;
                controls.enableZoom = true; // Enable zooming
                controls.enablePan = true; // Enable panning
                controls.target.set(0, 0, 0); // Set the target to the center of the scene
                controls.update();

                if (animationIdRef.current) {
                    cancelAnimationFrame(animationIdRef.current);
                }

                // Animation loop
                const animate = () => {
                    animationIdRef.current = requestAnimationFrame(animate);
                    controls.update();
                    renderer.render(scene, camera);
                    // composer.render(); // Use composer instead of renderer
                }
                animate();

                // when its finished loading and rendering, set loading to false

            } catch (err) {
                console.error("Error fetching 3D file:", err);
                setError("Error loading 3D model");
            }
        };

        fetchAndLoad();
    }, [partNumber, part, width, height, lastLoadedPartNumber]);

    useEffect(() => {
        // Cleanup function to dispose of resources and remove event listeners
        return () => {
            cleanup();
        };
    }, []);

    const cleanup = () => {
        console.log("Cleaning up Part3DViewer resources");
        if (objectRef.current) {
            objectRef.current.traverse((child) => {
                if (child.isMesh && child.material) {
                    child.material.dispose(); // Dispose of the material
                }
            });
            objectRef.current.geometry?.dispose(); // Dispose of the geometry
            sceneRef.current.remove(objectRef.current); // Remove the object from the scene
            objectRef.current = null; // Clear the reference
        }
        if (animationIdRef.current) {
            cancelAnimationFrame(animationIdRef.current); // Cancel the animation frame
            animationIdRef.current = null; // Clear the reference
        }
        if (rendererRef.current) {
            rendererRef.current.dispose(); // Dispose of the renderer
            rendererRef.current = null; // Clear the reference
        }
        if (cameraRef.current) {
            cameraRef.current = null; // Clear the camera reference
        }
        if (sceneRef.current) {
            sceneRef.current.traverse((child) => {
                if (child.isMesh) {
                    if (child.geometry) {
                        child.geometry.dispose();
                    }
                    if (child.material) {
                        if (Array.isArray(child.material)) {
                            child.material.forEach(material => {
                                if (material.map) material.map.dispose();
                                if (material.envMap) material.envMap.dispose();
                                material.dispose();
                            });
                        } else {
                            if (child.material.map) child.material.map.dispose();
                            if (child.material.envMap) child.material.envMap.dispose();
                            child.material.dispose();
                        }
                    }
                }
            });
            sceneRef.current.clear();
            sceneRef.current = null;
        }
        if (canvasRef.current) {
            while (canvasRef.current.firstChild) {
                canvasRef.current.removeChild(canvasRef.current.firstChild); // Remove all children from the canvas
            }
        }
    };

    const changeColor = (newColor) => {
        const intColor = parseInt(newColor.replace("#", ""), 16);
        if (objectRef.current) {
            objectRef.current.traverse((child) => {
                if (child.isMesh && child.material) {
                    child.material.color.set(intColor);
                    child.material.needsUpdate = true;
                }
            });
        }
        setColor(newColor);
    }

    const changeMetalness = (newValue) => {
        if (objectRef.current) {
            objectRef.current.traverse((child) => {
                if (child.isMesh && child.material) {
                    child.material.metalness = newValue;
                    child.material.needsUpdate = true;
                }
            });
        }
        setMetalness(newValue);
    }

    const changeRoughness = (newValue) => {
        if (objectRef.current) {
            objectRef.current.traverse((child) => {
                if (child.isMesh && child.material) {
                    child.material.roughness = newValue;
                    child.material.needsUpdate = true;
                }
            });
        }
        setRoughness(newValue);
    }


    const handleSaveSettings = () => {
        if (!part) return;
        if (!userHasPermission) {
            setError(t("You do not have permission to save part settings."));
            return;
        }
        const updatedPart = {
            part_number: partNumber,
            color: parseInt(color.replace("#", ""), 16),
            metalness: metalness,
            roughness: roughness,
        };
        updatePart({ part_number: partNumber, ...updatedPart })
            .unwrap()
            .then(() => {
                setError(t("Part settings updated successfully."));
            })
            .catch((err) => {
                console.error("Error updating part settings:", err);
                setError(t("Failed to update part settings."));
            });
    };



    if (isError) {
        console.error("Error fetching part data:", fetchError);
        return <div>Error loading part data</div>;
    }


    return (
        <div ref={canvasRef} style={{
            width: "100%", height: "100%",
            backgroundColor: theme.palette.mode === "dark" ? "#303030" : "#f0f0f0",
            // prevent dragging of the canvas
            userSelect: "none", WebkitUserSelect: "none", MozUserSelect: "none", msUserSelect: "none",
            position: "relative", overflow: "hidden", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", outline: "none", touchAction: "none",
        }} onKeyDown={(e) => {
            if (e.key === "Escape" && onClose) {
                e.preventDefault();
                onClose();
            }
        }}>
            {isLoading && (
                <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", zIndex: 1000 }}>
                    <CircularProgress />
                </div>
            )}

            <IconButton
                onClick={() => {
                    if (onClose) {
                        onClose();
                    }
                }}
                style={{
                    position: "absolute",
                    top: 10,
                    right: 10,
                    zIndex: 1000,
                    color: theme.palette.text.primary
                }}
            >
                <CloseIcon />
            </IconButton>

            {
                userHasPermission && part && (
                    <Accordion sx={{ position: "absolute", bottom: 0, left: 0, zIndex: 1, width: "400px" }}>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                            <Typography variant="h6">{t("Part Settings")}</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={12}>
                                    <Typography variant="subtitle1">{t("Part Color")}</Typography>
                                    <HexColorPicker
                                        color={color}
                                        onChange={(newColor) => {
                                            changeColor(newColor);
                                        }}
                                        style={{ width: "100%" }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Slider
                                        value={metalness}
                                        onChange={(_, newValue) => changeMetalness(newValue)}
                                        aria-labelledby="metalness-slider"
                                        valueLabelDisplay="auto"
                                        step={0.01}
                                        min={0}
                                        max={1}
                                        marks
                                        style={{ width: "100%" }}
                                    />
                                    <TextField
                                        label={t("Metalness")}
                                        type="number"
                                        value={metalness}
                                        onChange={(e) => changeMetalness(parseFloat(e.target.value))}
                                        inputProps={{ step: 0.01, min: 0, max: 1 }}
                                        fullWidth
                                        size="small"
                                        variant="standard"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Slider
                                        value={roughness}
                                        onChange={(e, newValue) => changeRoughness(newValue)}
                                        aria-labelledby="roughness-slider"
                                        valueLabelDisplay="auto"
                                        step={0.01}
                                        min={0}
                                        max={1}
                                        marks
                                        style={{ width: "100%" }}
                                    />
                                    <TextField
                                        label={t("Roughness")}
                                        type="number"
                                        value={roughness}
                                        onChange={(e) => changeRoughness(parseFloat(e.target.value))}
                                        inputProps={{ step: 0.01, min: 0, max: 1 }}
                                        fullWidth
                                        size="small"
                                        variant="standard"
                                    />
                                </Grid>
                            </Grid>
                        </AccordionDetails>
                        <AccordionActions>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => handleSaveSettings()}
                                disabled={updateResponse.isLoading || !userHasPermission}
                            >
                                {t("Save settings")}
                            </Button>
                        </AccordionActions>
                    </Accordion>
                )}
            <Snackbar
                open={!!error}
                autoHideDuration={6000}
                onClose={() => setError(null)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setError(null)}
                    severity="error"
                    sx={{ width: '100%' }}
                >
                    {error}
                </Alert>
            </Snackbar>
            <Snackbar
                open={updateResponse.isSuccess}
                autoHideDuration={6000}
                onClose={() => updateResponse.reset()}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => updateResponse.reset()}
                    severity="success"
                    sx={{ width: '100%' }}
                >
                    {t("Part settings updated successfully.")}
                </Alert>
            </Snackbar>

            {error && <div>{error}</div>}
        </div>
    );
};
export default Part3DViewer